import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import useSearchMutaion from '../../../../hooks/apis/useSearchMutaion';
import { apiRequest } from '../../../../Services/Common/ApiRequest';
import Global from '../../../../Services/Common/Global';
import useSaveMutation from '../../../../hooks/apis/useSaveMutaion';
import { handleQuerySuccess } from '../../../../hooks/useQuriesSuccess';
import { mapParamsBySchema, mapRowsBySchema } from '../../../../util';
import { SAM_BASIC_02_WorkType_Schema } from './SAM_BASIC_02_WorkType_Schema';

/**
 * SAM_BASIC_02_WorkType(작업종류별 항목 관리) 관련 React Query 훅 모음
 */

// 작업종류별 항목 조회
export const useSearchWorkTypeItems = (options = {}) => {
  const { onSuccess, context } = options;

  // queryKey에 context 정보 포함 (동적으로 업데이트됨)
  const queryKey = [
    'SafetyWorkManagement',
    'getSamBasic02WorkTypeItemR1',
    context?.search?.custCd || '',
  ];

  const query = useQuery({
    queryKey,
    queryFn: () => {
      // queryFn 내에서 최신 context로 params 생성
      const params = mapParamsBySchema(
        SAM_BASIC_02_WorkType_Schema.mapper.search1,
        context
      );
      return apiRequest(
        'post',
        Global.urls.SafetyWorkManagement.getSamBasic02WorkTypeItemR1,
        params
      );
    },
    enabled: false, // 자동 실행 방지
  });

  useEffect(() => {
    if (!query.isSuccess) return;

    handleQuerySuccess(query.data, {
      onSuccess: async (rows) => {
        await onSuccess?.(rows);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.dataUpdatedAt, query.data]);

  return query;
};

// 작업종류 코드 조회 (GetCodeInfo API 사용)
export const useSearchWorkTypeCodes = (options = {}) => {
  return useSearchMutaion(
    () => {
      return apiRequest(
        'post',
        Global.urls.Utility.getCodeInfo,
        {}
      );
    },
    options
  );
};

// 사업장별 커스텀 작업종류 조회
export const useSearchWorkTypeCust = (options = {}) => {
  const { onSuccess, context } = options;

  const queryKey = [
    'SafetyWorkManagement',
    'getSamBasic02WorkTypeCustR1',
    context?.search?.custCd || '',
  ];

  const query = useQuery({
    queryKey,
    queryFn: () => {
      const params = mapParamsBySchema(
        SAM_BASIC_02_WorkType_Schema.mapper.searchCust,
        context
      );
      return apiRequest(
        'post',
        Global.urls.SafetyWorkManagement.getSamBasic02WorkTypeCustR1,
        params
      );
    },
    enabled: false,
  });

  useEffect(() => {
    if (!query.isSuccess) return;

    handleQuerySuccess(query.data, {
      onSuccess: async (rows) => {
        await onSuccess?.(rows);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.dataUpdatedAt, query.data]);

  return query;
};

// 사업장별 커스텀 작업종류 조회 (mutation — 팝업 등에서 표준 코드와 병렬 조회)
export const useSearchWorkTypeCustMutation = (options = {}) => {
  return useSearchMutaion(async (context) => {
    const params = mapParamsBySchema(
      SAM_BASIC_02_WorkType_Schema.mapper.searchCust,
      context || {}
    );
    return apiRequest(
      'post',
      Global.urls.SafetyWorkManagement.getSamBasic02WorkTypeCustR1,
      params
    );
  }, options);
};

// 사업장별 커스텀 작업종류 등록
export const useSaveWorkTypeCust = (options = {}) => {
  const contextFromOptions = {
    search: options.search || {},
    userInfo: options.userInfo || {},
    ...options,
  };

  return useSaveMutation(async (row) => {
    const payload = mapParamsBySchema(
      SAM_BASIC_02_WorkType_Schema.mapper.saveCust,
      { ...contextFromOptions, ...row }
    );
    return apiRequest(
      'post1',
      Global.urls.SafetyWorkManagement.saveSamBasic02WorkTypeCustU1,
      payload
    );
  }, options);
};

// 사업장별 커스텀 작업종류 삭제
export const useDeleteWorkTypeCust = (options = {}) => {
  const contextFromOptions = {
    userInfo: options.userInfo || {},
    ...options,
  };

  return useSaveMutation(async (row) => {
    const payload = mapParamsBySchema(
      SAM_BASIC_02_WorkType_Schema.mapper.deleteCust,
      { ...contextFromOptions, ...row }
    );
    return apiRequest(
      'post1',
      Global.urls.SafetyWorkManagement.deleteSamBasic02WorkTypeCustD1,
      payload
    );
  }, options);
};

// 작업종류별 항목 저장 (삭제 포함)
export const useSaveWorkTypeItems = (options = {}) => {
  // options에서 context 추출
  const contextFromOptions = {
    search: options.search || {},
    userInfo: options.userInfo || {},
    ...options,
  };

  return useSaveMutation(async (data) => {
    // data는 { saveItems: [], deleteItems: [] } 형태
    const { saveItems = [], deleteItems = [] } = data;

    // SaveItems 매핑
    const mappedSaveItems = mapRowsBySchema(
      saveItems,
      SAM_BASIC_02_WorkType_Schema.mapper.save1,
      contextFromOptions
    );

    // DeleteItems 매핑
    const mappedDeleteItems = deleteItems.length > 0
      ? mapRowsBySchema(
          deleteItems,
          SAM_BASIC_02_WorkType_Schema.mapper.delete1,
          contextFromOptions
        )
      : [];

    // 서버 API 형식에 맞춰 요청
    const payload = {
      SaveItems: mappedSaveItems,
      DeleteItems: mappedDeleteItems.length > 0 ? mappedDeleteItems : null,
    };


    const response = await apiRequest(
      'post1',
      Global.urls.SafetyWorkManagement.saveSamBasic02WorkTypeItemU1,
      payload
    );

    return response;
  }, options);
};
